import React, { useEffect, useState } from "react";
import { toast } from "sonner";
import { Video, Loader2, Link2, X, Film } from "lucide-react";
import { Link } from "react-router-dom";
import api, { API } from "@/lib/api";

/** "Video Resume" -- lets the learner MANUALLY attach one of their existing Cloudivo Meet
 * recordings as a short video introduction on their resume (per user's explicit choice: no
 * auto-attach on recording-complete). Shown on both Wizard Step 1 and the Advanced editor. */
export default function VideoIntroCard({ draft, setDraft }) {
  const [recordings, setRecordings] = useState(null);
  const [picking, setPicking] = useState(false);
  const [saving, setSaving] = useState(false);

  useEffect(() => {
    api.get("/meetings/recordings/mine").then(({ data }) => setRecordings(data)).catch(() => setRecordings([]));
  }, []);

  const linked = recordings?.find((r) => r.id === draft.video_intro_recording_id);

  const linkRecording = async (recordingId) => {
    setSaving(true);
    try {
      const { data } = await api.put("/resume-builder/draft", { ...draft, video_intro_recording_id: recordingId });
      setDraft(data);
      setPicking(false);
      toast.success("Video intro linked to your resume");
    } catch {
      toast.error("Could not link this recording right now");
    } finally {
      setSaving(false);
    }
  };

  const unlink = async () => {
    setSaving(true);
    try {
      const { data } = await api.put("/resume-builder/draft", { ...draft, video_intro_recording_id: "" });
      setDraft(data);
      toast.success("Video intro removed");
    } catch {
      toast.error("Could not remove the video intro right now");
    } finally {
      setSaving(false);
    }
  };

  return (
    <div className="glass-panel rounded-2xl p-4" data-testid="resume-video-intro-card">
      <h3 className="flex items-center gap-2 text-sm font-heading font-semibold text-white mb-2">
        <Video className="w-4 h-4 text-indigo-400" /> Video Introduction
      </h3>

      {draft.video_intro_recording_id ? (
        <div className="space-y-2">
          <video
            src={`${API}/meetings/recordings/${draft.video_intro_recording_id}/stream`}
            controls className="w-full rounded-lg max-h-52 bg-black" data-testid="resume-video-intro-preview"
          />
          <div className="flex items-center justify-between text-[11px] text-slate-400 gap-2">
            <span className="truncate flex items-center gap-1" data-testid="resume-video-intro-title"><Film className="w-3 h-3 shrink-0" /> {linked?.title || "Linked recording"}</span>
            <button onClick={unlink} disabled={saving} data-testid="resume-video-intro-unlink-button" className="flex items-center gap-1 text-rose-400 hover:text-rose-300 disabled:opacity-60 shrink-0">
              <X className="w-3 h-3" /> Remove
            </button>
          </div>
        </div>
      ) : picking ? (
        <div className="space-y-2">
          {recordings === null ? (
            <p className="text-xs text-slate-400 flex items-center gap-1.5"><Loader2 className="w-3 h-3 animate-spin" /> Loading your recordings...</p>
          ) : recordings.length === 0 ? (
            <p className="text-xs text-slate-400" data-testid="resume-video-intro-empty">
              No Cloudivo Meet recordings yet. <Link to="/meetings" className="text-indigo-400 hover:text-indigo-300">Record a short intro</Link>, then come back to link it here.
            </p>
          ) : (
            <div className="max-h-48 overflow-y-auto space-y-1.5" data-testid="resume-video-intro-recording-list">
              {recordings.map((r) => (
                <button
                  key={r.id} onClick={() => linkRecording(r.id)} disabled={saving} data-testid={`resume-video-intro-option-${r.id}`}
                  className="w-full text-left bg-white/5 hover:bg-white/10 disabled:opacity-60 rounded-lg px-3 py-2 text-xs text-slate-200 transition-colors"
                >
                  <p className="font-semibold truncate">{r.title}</p>
                  <p className="text-slate-500 text-[10px]">{new Date(r.created_at).toLocaleDateString()} &middot; {r.meeting_title}</p>
                </button>
              ))}
            </div>
          )}
          <button onClick={() => setPicking(false)} data-testid="resume-video-intro-cancel-button" className="text-[11px] text-slate-500 hover:text-slate-300">Cancel</button>
        </div>
      ) : (
        <button
          onClick={() => setPicking(true)} data-testid="resume-video-intro-pick-button"
          className="flex items-center justify-center gap-1.5 w-full bg-indigo-500 hover:bg-indigo-400 text-white text-xs font-semibold px-4 py-2 rounded-lg transition-colors"
        >
          <Link2 className="w-3.5 h-3.5" /> Link a Cloudivo Meet Recording
        </button>
      )}
      <p className="text-[10px] text-slate-500 mt-2">Record a 30-60s intro in Cloudivo Meet, then link it here -- it'll play on your shared resume link.</p>
    </div>
  );
}
